import { minBy, every, last, first } from 'lodash';

// ***************************************************************************************
// onMoveCursorLetterByLetter permet de faire bouger le curseur lettre par lettre
// ***************************************************************************************

export function onMoveCursorLetterByLetter(key) {
  // si le curseur n'est pas sur le mot ou est sur le mot validé,
  // on prend le prochain mot non validé
  if (!this.currentWord || this.currentWord.submitted) {
    const nextWord = this.script.find((x) => !x.submitted);
    if (nextWord) {
      this.currentWordIndex = nextWord.index;
      this.currentLineIndex = nextWord.indexLine;

      this.scrollTo(nextWord.indexLine);
      this.onMove();

      return;
    }
  }

  switch (key) {
    case 'LEFT':

      if (this.currentWord?.rtl) {
        this.onMoveCursorToRight();
        break;
      }

      this.onMoveCursorToLeft();
      break;
    case 'RIGHT':

      if (this.currentWord?.rtl) {
        this.onMoveCursorToLeft();
        break;
      }
      
      this.onMoveCursorToRight();
      break;
    case 'DOWN':
      this.onMoveCursorToDown();
      break;
    case 'UP':
      this.onMoveCursorToUp();
      break;
    default:
      throw new Error('key not implemented');
  }
}

// ***************************************************************************************
// onMoveCursorToRight bouge le curseur vers la droite
// ***************************************************************************************

export function onMoveCursorToRight() {
  // On supprime la sélection et on positionne le curseur
  if (this.underSelection) {
    this.underSelection = false;

    if (this.wordsToSelect.length || this.lettersToSelect.length) {
      let y = 0;

      if (this.wordsToSelect.length) {
        const lastWord = last(this.wordsToSelect);

        this.currentWordIndex = lastWord.index;
        this.currentLineIndex = lastWord.indexLine;

        if (lastWord.letters.length) {
          this.currentLetterIndex = last(lastWord.letters).indexLetter;
        }
      } else {
        const firstLetter = last(this.lettersToSelect);
        const word = this.script[firstLetter.indexWord];

        this.currentWordIndex = word.index;
        this.currentLineIndex = word.indexLine;
        this.currentLetterIndex = firstLetter.indexLetter;
        y = word.y;
      }

      if (y === 0 || this.height < y + this.lineHeight) {
        this.scrollTo(this.currentLineIndex);
        return;
      }

      this.onRefreshCanvas(false);
      this.onMove();
    }

    return;
  }

  // Si c'est le dernier mot et la dernière lettre du texte, ne rien faire
  if (
    (this.currentWord.text === '' || this.currentLetter.lastChar) &&
    this.currentWord.index === this.script.length - 1
  ) {
    return;
  }

  // si c'est le dernier caractère de la ligne alors passer à la ligne suivante
  if (this.currentLetter.lastChar) {
    this.currentLineIndex++;
    this.currentLetterIndex = 0;
    this.currentWordIndex++;

    // scroll to down
    this.scrollToDown(this.script[this.currentWordIndex]);

    this.onMove();
    return;
  }

  if (!this.currentLetter) {
    return;
  }

  // S'il n'y a pas de lettres ou si c'est la dernière lettre, alors basculer sur le prochain mot
  if (
    this.currentWord.letters.length === 0 ||
    this.currentLetter.index === this.currentWord.letters.length - 1
  ) {
    this.currentWordIndex++;
  }

  // si le prochain mot est à la ligne suivante (à cause de la taille de la fenêtre)
  const nextWord = this.script[this.currentWordIndex];

  if (nextWord.indexLine !== this.currentLineIndex) {
    this.currentLineIndex++;
    this.currentLetterIndex = 0;

    // scroll to down
    this.scrollToDown(nextWord);
    this.onMove();
    return;
  }

  this.currentLetterIndex++;
  this.onMove();
}

// ***************************************************************************************
// onMoveCursorToLeft bouge le curseur vers la gauche
// ***************************************************************************************
export function onMoveCursorToLeft() {
  // On supprime la sélection et on positionne le curseur
  if (this.underSelection) {
    this.underSelection = false;

    if (this.wordsToSelect.length || this.lettersToSelect.length) {
      let y = 0;

      if (this.wordsToSelect.length) {
        const firstWord = first(this.wordsToSelect);
        this.currentWordIndex = firstWord.index;
        this.currentLetterIndex = first(firstWord.letters).indexLetter;
        this.currentLineIndex = firstWord.indexLine;

        y = firstWord.y;
      } else {
        const firstLetter = first(this.lettersToSelect);
        const word = this.script[firstLetter.indexWord];

        this.currentWordIndex = word.index;
        this.currentLineIndex = word.indexLine;
        this.currentLetterIndex = firstLetter.indexLetter;
        y = word.y;
      }

      if (y === 0 || y === this.metricY) {
        this.scrollTo(this.currentLineIndex);
        return;
      }

      this.onRefreshCanvas(false);
      this.onMove();
    }

    return;
  }

  if (
    this.currentWordIndex === 0 &&
    this.currentLineIndex === 0 &&
    this.currentLetterIndex === 0
  ) {
    return;
  }

  if (!this.currentLetter) {
    return;
  }

  // basculer au précédent mot
  if (
    this.currentWord.letters.length === 0 ||
    (this.currentLetter.index === 0 && this.currentLetter.indexLetter !== 0)
  ) {
    const prevWord = this.script[this.currentWordIndex - 1];
    if (prevWord && prevWord.submitted) {
      return;
    }

    this.currentLetterIndex--;
    this.currentWordIndex--;

    this.onMove();
    return;
  }

  // basculer à la ligne précédente
  if (this.currentLetter.indexLetter === 0) {
    const prevWord = this.script[this.currentWordIndex - 1];
    if (prevWord && prevWord.submitted) {
      return;
    }

    const letters = this.script
      .filter((word) => word.indexLine === this.currentLineIndex - 1)
      .flatMap((word) => word.letters.map((l) => l.letter));

    this.currentLetterIndex = letters.length - 1;
    this.currentLineIndex--;
    this.currentWordIndex--;

    // Scroll to up
    this.scrollToUp(this.script[this.currentWordIndex]);
    this.onMove();

    return;
  }

  this.currentLetterIndex--;
  this.onMove();
}

// ***************************************************************************************
// onMoveCursorToDown bouge le curseur vers le bas
// ***************************************************************************************
export function onMoveCursorToDown() {
  // On supprime la sélection et on positionne le curseur
  if (this.underSelection) {
    this.underSelection = false;

    if (this.wordsToSelect.length || this.lettersToSelect.length) {
      let y = 0;

      if (this.wordsToSelect.length) {
        const lastWord = last(this.wordsToSelect);

        this.currentWordIndex = lastWord.index;
        this.currentLineIndex = lastWord.indexLine;

        if (lastWord.letters.length) {
          this.currentLetterIndex = last(lastWord.letters).indexLetter;
        }
      } else {
        const firstLetter = last(this.lettersToSelect);
        const word = this.script[firstLetter.indexWord];

        this.currentWordIndex = word.index;
        this.currentLineIndex = word.indexLine;
        this.currentLetterIndex = firstLetter.indexLetter;
        y = word.y;
      }

      if (y === 0 || this.height < y + this.lineHeight) {
        this.scrollTo(this.currentLineIndex);
        return;
      }

      this.onRefreshCanvas(false);
      this.onMove();
    }

    return;
  }

  //Si c'est la dernière ligne, ne rien faire
  const lastLine = this.script[this.script.length - 1].indexLine;
  if (this.currentLineIndex === lastLine) {
    return;
  }

  // trouver le prochain mot de la prochaine ligne
  const nextLine = this.script.filter(
    (elt) => elt.indexLine === this.currentLineIndex + 1
  );

  if (!nextLine.length) {
    return;
  }

  // Trouver le mot contenant le caractère courant
  const nextWord = nextLine.find(
    (w) => w.x <= this.currentLetter.x && this.currentLetter.x <= w.x + w.width
  );

  if (nextWord) {
    // On va prendre la lettre la plus proche des coordonnées x
    const nextLetter = minBy(nextWord.letters, (letter) =>
      Math.abs(letter.x - this.currentLetter.x)
    );

    if (!nextLetter) {
      return;
    }

    this.currentLetterIndex = nextLetter.indexLetter;
    this.currentWordIndex = nextWord.index;
    this.currentLineIndex = nextWord.indexLine;
  } else {
    // la ligne au dessous a une phrase plus courte donc prendre la dernière lettre de la ligne
    const lastWord = last(nextLine);
    if (!lastWord) {
      return;
    }

    if (lastWord.letters && lastWord.letters.length) {
      this.currentLetterIndex = last(lastWord.letters).indexLetter;
    }

    this.currentWordIndex = lastWord.index;
    this.currentLineIndex = lastWord.indexLine;
  }

  // Scroll vers le bas
  this.scrollToDown(this.script[this.currentWordIndex]);
  this.onMove();
}

// ***************************************************************************************
// onMoveCursorToUp bouge le curseur vers le haut
// ***************************************************************************************
export function onMoveCursorToUp() {
  // On supprime la sélection et on positionne le curseur
  if (this.underSelection) {
    this.underSelection = false;

    if (this.wordsToSelect.length || this.lettersToSelect.length) {
      let y = 0;

      if (this.wordsToSelect.length) {
        const firstWord = first(this.wordsToSelect);
        this.currentWordIndex = firstWord.index;
        this.currentLetterIndex = first(firstWord.letters).indexLetter;
        this.currentLineIndex = firstWord.indexLine;

        y = firstWord.y;
      } else {
        const firstLetter = first(this.lettersToSelect);
        const word = this.script[firstLetter.indexWord];

        this.currentWordIndex = word.index;
        this.currentLineIndex = word.indexLine;
        this.currentLetterIndex = firstLetter.indexLetter;
        y = word.y;
      }

      if (y === 0 || y === this.metricY) {
        this.scrollTo(this.currentLineIndex);
        return;
      }

      this.onRefreshCanvas(false);
      this.onMove();
    }

    return;
  }

  if (this.currentLineIndex === 0) {
    return;
  }

  const prevLigne = this.script.filter(
    (elt) => elt.indexLine === this.currentLineIndex - 1
  );

  if (!prevLigne || !this.currentLetter) {
    return;
  }

  const getLetters = every(prevLigne, 'letters');

  // Si les tableaux de lettres n'existent pas
  if (!getLetters) {
    this.addLettersToNewLine(prevLigne);
  }

  // On va prendre la lettre la plus proche des coordonnées x
  const prevLetter = minBy(
    prevLigne.flatMap((x) => x.letters),
    (letter) => Math.abs(letter.x - this.currentLetter.x)
  );

  if (prevLetter) {
    const prevWord = this.script[prevLetter.indexWord];
    if (prevWord && prevWord.submitted) {
      const lastWordNotSubmitted = this.script.find((x) => !x.submitted);
      if (
        !lastWordNotSubmitted ||
        lastWordNotSubmitted.indexLine >= this.currentLineIndex
      ) {
        return;
      }

      this.currentWordIndex = lastWordNotSubmitted.index;
      this.currentLineIndex = lastWordNotSubmitted.indexLine;
      this.takeFirstLetter = true;
    } else {
      this.currentLetterIndex = prevLetter.indexLetter;
      this.currentLineIndex = prevWord.indexLine;
      this.currentWordIndex = prevLetter.indexWord;
    }
  } else {
    // la ligne au dessus a une phrase plus courte donc prendre la dernière lettre de la ligne
    const lastWord = last(prevLigne);

    if (lastWord && lastWord.submitted) {
      return;
    }

    this.currentWordIndex = lastWord.index;
    this.currentLineIndex = lastWord.indexLine;

    if (lastWord.letters.length) {
      this.currentLetterIndex = last(lastWord.letters).indexLetter;
    }
  }

  // scroll vers le haut
  this.scrollToUp(this.currentWord);
  this.onMove();
}

// ***************************************************************************************
// Lorsqu'on fait CTRL+P (PREVPOSITION), on revient à la position précédente du curseur
// ***************************************************************************************
export function onPreviousPositionCursor() {
  const lastPosition = last(this.prevPositionsCursor);
  if (!lastPosition) {
    return;
  }

  if (
    (this.currentWordIndex === lastPosition.indexWord &&
      this.currentLetterIndex === lastPosition.indexLetter &&
      this.currentLineIndex === lastPosition.indexLine) ||
    lastPosition.submitted
  ) {
    if (this.prevPositionsCursor.length > 1) {
      this.prevPositionsCursor.pop();
      this.onPreviousPositionCursor();
    }
    return;
  }

  const currentPosition = {
    indexLetter: this.currentLetterIndex,
    indexWord: this.currentWordIndex,
    indexLine: this.currentLineIndex,
  };

  this.currentLetterIndex = lastPosition.indexLetter;
  this.currentWordIndex = lastPosition.indexWord;
  this.currentLineIndex = lastPosition.indexLine;

  if (currentPosition.indexLine !== lastPosition.indexLine) {
    const nextWord = this.script[this.currentWordIndex];

    if (
      nextWord.y === 0 ||
      nextWord.y === this.metricY ||
      this.height < nextWord.y + this.lineHeight
    ) {
      this.scrollOffsetLine = this.currentLineIndex;
      this.scrollTo(this.scrollOffsetLine);
    }
  }

  if (this.prevPositionsCursor.length > 1) {
    this.prevPositionsCursor.pop();
  }
  this.onMove();
}

// ***************************************************************************************
// onGoToStartOrEndOfWord permet d'aller au début ou à la fin du mot
// ***************************************************************************************
export function onGoToStartOrEndOfWord(key) {
  const { letters } = this.currentWord;

  const letterIndex =
    key === 'STARTWORD'
      ? first(letters).indexLetter
      : last(letters).indexLetter;

  this.currentLetterIndex = letterIndex;
  this.onMove();
}

// ***************************************************************************************
// onGoToHomeOrEnd permet d'aller à la première page ou à la dernière page du texte
// ***************************************************************************************
export function onGoToHomeOrEnd(key) {
  if (key === 'HOME') {
    this.currentLineIndex = 0;
    this.currentWordIndex = 0;
    this.currentLetterIndex = 0;

    this.scrollOffsetLine = 0;
  } else {
    const lastWord = last(this.script);

    this.currentLineIndex = lastWord.indexLine;
    this.currentWordIndex = lastWord.index;

    this.scrollOffsetLine = this.state.maxScrollValue;
  }

  this.scrollTo(this.scrollOffsetLine);
}

// ***************************************************************************************
// onGoToPageUpOrDown permet d'aller à la page suivante ou à la page précédente du texte
// ***************************************************************************************
export function onGoToPageUpOrDown(key) {
  let newLineIndex = this.currentLineIndex;
  if (key === 'PGUP') {
    newLineIndex = newLineIndex - this.nbVisibleLines;

    if (newLineIndex < 0) {
      newLineIndex = 0;
    }
  } else {
    newLineIndex = newLineIndex + this.nbVisibleLines;

    if (newLineIndex > this.state.totalLines - this.nbVisibleLines) {
      newLineIndex = this.state.totalLines - this.nbVisibleLines;
    }
  }

  const nextWord = this.script.find((word) => word.indexLine === newLineIndex);
  if (!nextWord) {
    return;
  }

  this.currentLineIndex = nextWord.indexLine;
  this.currentWordIndex = nextWord.index;
  this.currentLetterIndex = 0;

  this.scrollTo(this.currentLineIndex);
}

// ***************************************************************************************
// onGoToSentence permet d'aller au début ou à la fin de la phrase
// ***************************************************************************************
export function onGoToSentence(key) {
  const indexLine =
    this.currentWord.parentIndexLine ?? this.currentWord.indexLine;

  const words = this.getWordsByIndexLine(indexLine);

  if (key === 'STARTSENTENCE') {
    const firstWord = first(words);

    this.currentWordIndex = firstWord.index;
    this.currentLineIndex = firstWord.indexLine;

    this.currentLetterIndex = 0;
  } else {
    const lastWord = last(words);
    this.currentWordIndex = lastWord.index;
    this.currentLineIndex = lastWord.indexLine;
    this.currentLetterIndex = last(lastWord.letters)?.indexLetter;
  }

  // Si les lignes sont en dehors du canvas, on doit redessiner le canvas
  const goToWord = this.script[this.currentWordIndex];
  if (goToWord.y === 0 || this.height < goToWord.y + this.lineHeight) {
    if (key === 'STARTSENTENCE') {
      this.scrollTo(this.scrollOffsetLine - 1);
    } else {
      this.scrollTo(this.scrollOffsetLine + 1);
    }
    this.onRefreshCanvas(true);
  }

  this.onMove();
}

// ***************************************************************************************
// onGoToSpeakerDialogue permet de naviguer du dialogue en dialogue du speaker
// ***************************************************************************************
export function onGoToSpeakerDialogue(key) {
  if (!this.currentWord.speakerId){
    return;
  }

   const indexLine =
    this.currentWord.parentIndexLine ?? this.currentWord.indexLine;

  const words = this.script.filter(
    (x) => x.speakerId === this.currentWord.speakerId
  );

  if (!words.length) {
    return;
  }

  let found = false;
  if (key === 'GOTONEXTDIALOGUE') {
    const nextWord = words.find((x) => x.indexLine > indexLine && x.newline);

    if (nextWord) {
      this.currentWordIndex = nextWord.index;
      this.currentLineIndex = nextWord.indexLine;
      this.currentLetterIndex = 0;
      found = true;
    }
  } else {
    const prevWord = words.findLast(
      (x) => x.indexLine < indexLine && x.newline
    ) ?? words[0];
    if (prevWord) {
      this.currentWordIndex = prevWord.index;
      this.currentLineIndex = prevWord.indexLine;
      this.currentLetterIndex = 0;
      found = true;
    }
  }

  if (found) {
    // Si les lignes sont en dehors du canvas, on doit redessiner le canvas
    const goToWord = this.script[this.currentWordIndex];
    if (goToWord.y === 0 || this.height < goToWord.y + this.lineHeight) {
      this.scrollTo(this.currentLineIndex);
    }

    this.onMove();
  }
}